import {
  datadogReporter as ddReporter,
  METRICS_DESTINATIONS,
  EventDataProps,
  ReporterOptions
} from '@cvent/sli-nextjs-metrics';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import getConfig from 'next/config';
import { getTransformer, Reporter } from './metricDataTransformers';
import transformDeprecatedDatadogTags from './transformDeprecatedDatadogTags';

const LOG = LoggerFactory.create('datadogReporter');
const { publicRuntimeConfig } = getConfig();

type DatadogReporterOptions = ReporterOptions & {
  metadata?: {
    datadog?: {
      eventId?: string;
    };
  };
};

type DatadogData = EventDataProps & {
  downstreamName?: string;
};

/**
 * Initialize a datadog reporter for the base data. The based data is used to initialize the SLI metrics which
 * passes the base data as global context to DD RUM. The base data is also passed to the MetricsClient and there
 * are issues where the cvent_ prefix is added multiple times to base data if we don't ignore the base data keys
 * TODO HUB-100043 to cleanup the extra logic once all the analytics facts are migrated
 *
 * @param baseData The base data used to initialize the Metrics component. It will be ignored from applying the
 * transformation via transformDeprecatedDatadogTags.
 */
export const getDatadogReporter = (baseData: Record<string, unknown>) => {
  /**
   * Overrides reporter from metrics' package to check if the event should be tracked based on the running environment
   */
  return (id: string, data: DatadogData, options?: DatadogReporterOptions) => {
    const { destinations, downstreamName, ...rest } = data || {};
    // If the metric has destinations, and datadog is not one of them, skip
    if (destinations && !destinations.includes(METRICS_DESTINATIONS.DATA_DOG)) {
      return;
    }
    let eventName = id;
    if (options?.metadata?.datadog?.eventId) {
      // if the datadog event id is overriden by metadata passed in options
      eventName = options?.metadata?.datadog?.eventId;
    } else if (downstreamName && typeof downstreamName === 'string') {
      // to avoid duplicate event names, the tracking-plan-publisher will append the plan name to all actions by
      // default to guarantee unique names for each action published to the same workspace in Rudderstack. The API
      // will add the "downstreamName" to the data which we should use as the id/event name.
      eventName = downstreamName;
    }

    const dataCopy = transformDeprecatedDatadogTags(
      getTransformer(
        Reporter.DATADOG,
        eventName
      )({
        ...rest,
        destinations
      }),
      Object.keys(baseData) // ignore the base
    ) as EventDataProps;

    if (publicRuntimeConfig.IS_DEV === 'true') {
      LOG.debug(`Mocked Event ${eventName} >>>> `, dataCopy);
      return;
    }

    ddReporter(eventName, dataCopy, options);
  };
};
